import React from 'react';
import {
  Archive,
  ArrowLeft,
  Code2,
  Download,
  MoreVertical,
  MessageSquareMore,
  RefreshCw,
} from 'lucide-react';

import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import SignalProcessingStatusBadge from '../../shared/SignalProcessingStatusBadge';

interface CaseWorkspaceHeaderProps {
  caseIdLabel: string;
  caseTitle: string;
  openedAt?: string | null;
  lastActiveAt?: string | null;
  productLabel?: string | null;
  signalStatus?: string;
  signalProcessingStatus?: string;
  signalProgressStage?: string | null;
  signalProgressPercent?: number | null;
  onBack: () => void;
  onViewSignal?: () => void;
  onExportPDF: () => void;
  onDownloadJSON: () => void;
  onProvideFeedback: () => void;
  onRerunSimulation: () => void;
  isRerunning?: boolean;
  onArchive: () => void;
}

const formatAbsoluteDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatRelativeTime = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const date = new Date(value).getTime();
  const diffInHours = Math.max(0, Math.floor((Date.now() - date) / (1000 * 60 * 60)));

  if (diffInHours < 1) {
    return 'just now';
  }

  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 8) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${Math.max(1, diffInMonths)} month${diffInMonths === 1 ? '' : 's'} ago`;
};

const CaseWorkspaceHeader: React.FC<CaseWorkspaceHeaderProps> = ({
  caseIdLabel,
  caseTitle,
  openedAt,
  lastActiveAt,
  productLabel,
  signalStatus,
  signalProcessingStatus,
  signalProgressStage,
  signalProgressPercent,
  onBack,
  onViewSignal,
  onExportPDF,
  onDownloadJSON,
  onProvideFeedback,
  onRerunSimulation,
  isRerunning = false,
  onArchive,
}) => {
  const openedLabel = formatAbsoluteDate(openedAt);
  const lastActiveLabel = formatRelativeTime(lastActiveAt);
  const showSignalStatus = signalStatus || signalProcessingStatus || signalProgressStage;

  return (
    <header className="mb-6 border-b border-border pb-4">
      <div className="mb-3 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Case Guidance</span>
          <span>/</span>
          <span className="font-semibold text-foreground">{caseIdLabel}</span>
        </button>

        <div className="flex items-center gap-2">
          {onViewSignal && (
            <Button variant="outline" size="sm" onClick={onViewSignal}>
              View record
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={onExportPDF}>
                <Download className="mr-2 h-4 w-4" />
                Export Guidance Pack (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownloadJSON}>
                <Code2 className="mr-2 h-4 w-4" />
                Download JSON
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRerunSimulation} disabled={isRerunning}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isRerunning ? 'animate-spin' : ''}`} />
                {isRerunning ? 'Starting Simulation...' : 'Rerun Simulation'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onProvideFeedback}>
                <MessageSquareMore className="mr-2 h-4 w-4" />
                Provide Feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onArchive} className="text-[var(--go-berry)]">
                <Archive className="mr-2 h-4 w-4" />
                Archive Case
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-jakarta text-xl font-extrabold tracking-[-0.01em] text-foreground">
            {caseIdLabel}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            {openedLabel && <span>Opened {openedLabel}</span>}
            {openedLabel && (lastActiveLabel || productLabel) && <span>&middot;</span>}
            {lastActiveLabel && <span>Last active {lastActiveLabel}</span>}
            {lastActiveLabel && productLabel && <span>&middot;</span>}
            {productLabel && <span>{productLabel}</span>}
            {showSignalStatus && (
              <SignalProcessingStatusBadge
                processingStatus={signalProcessingStatus}
                status={signalStatus}
                progressStage={signalProgressStage}
                progressPercent={signalProgressPercent}
              />
            )}
          </div>
          {caseTitle && (
            <p className="mt-3 max-w-4xl text-[13px] font-medium leading-[1.6] text-foreground">
              {caseTitle}
            </p>
          )}
        </div>

        {signalProgressPercent !== null && signalProgressPercent !== undefined && signalProgressPercent > 0 && (
          <Badge variant="secondary" className="shrink-0">
            {signalProgressPercent}% complete
          </Badge>
        )}
      </div>
    </header>
  );
};

export default CaseWorkspaceHeader;
